'use client'

import { MODAL_TYPE, Modal, type ModalAPI } from 'fukku/Modal'
import { ClientLabel } from 'labels/client'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { ReturnsDownloadForm } from 'my-purchases/components/ReturnsDownloadForm/ReturnsDownloadForm'
import { ORDER_TYPES } from 'my-purchases/constants/orderTypes'
import type { OrderDetails } from 'my-purchases/types/purchaseDetail'
import {
	sendOnClickFindStore,
	sendOnClickReturnButton,
} from 'my-purchases/utils/analytics/analyticsEvents'
import { isOrderReturnable } from 'my-purchases/utils/isOrderReturnable'
import dynamic from 'next/dynamic'
import { useMemo, useRef } from 'react'
import { ReturnMethodsInfoContainer } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/ReturnMethodsInfoContainer'
import { ReturnDaysText } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/ReturnDaysText/ReturnDaysText'
import { useReturnsInfo } from 'shipping-and-returns/hooks/useReturnsInfo/useReturnsInfo'
import { cx } from 'utils/cx'
import { dateFormatter } from 'utils/dateFormatter/dateFormatter'

import { OnlineReturnsInfo } from './components/OnlineReturnsInfo/OnlineReturnsInfo'

import styles from './ReturnsContainer.module.scss'
import texts from 'fukku/styles/texts.module.scss'

const StoreLocatorModal = dynamic(() =>
	import('maps/components/StoreLocatorModal/StoreLocatorModal').then(
		(mod) => mod.StoreLocatorModal
	)
)

export function ReturnsContainer({
	order,
	className,
}: {
	readonly order: OrderDetails
	readonly className?: string
}) {
	const { t } = useLabels()
	const returnMethodsModalRef = useRef<ModalAPI>(null)
	const storeLocatorModalRef = useRef<ModalAPI>(null)
	const { maximumReturnDays } = useReturnsInfo()
	const {
		country: { locale },
	} = useMasterData()

	const {
		active,
		detailOperations,
		status,
		type,
		maxReturnDate,
		id: orderId,
		purchaseId,
		unavailableReturnMethods,
		barcode,
	} = order

	const isReturnable = useMemo(
		() => isOrderReturnable(status, detailOperations),
		[status, detailOperations]
	)

	if (!active || (type === ORDER_TYPES.ONLINE && !isReturnable)) {
		return null
	}

	const returnDate =
		maxReturnDate && dateFormatter({ dateString: maxReturnDate, locale })

	const handleOpenReturnsModal = () => {
		sendOnClickReturnButton()
		returnMethodsModalRef.current?.show()
	}

	const handleOpenStoreLocatorModal = () => {
		storeLocatorModalRef.current?.show()
	}

	const handleFindStoreClick = () => {
		sendOnClickFindStore()
		handleOpenStoreLocatorModal()
	}

	const buttonProps = {
		onClick: handleFindStoreClick,
		className: styles.findStoreButton,
	}

	return (
		<div
			className={cx(styles.container, className)}
			data-testid='myPurchases.orderDetail.returns.container'
		>
			<h2 className={texts.titleL}>
				{t('myPurchases.online.details.returns.title')}
			</h2>
			{type === ORDER_TYPES.STORE && (
				<p>
					<ClientLabel buttonProps={buttonProps}>
						{t('myPurchases.store.details.returnInfo.text')}
					</ClientLabel>
				</p>
			)}
			{type === ORDER_TYPES.ONLINE && (
				<OnlineReturnsInfo
					maximumReturnDays={maximumReturnDays}
					returnDate={returnDate}
					handleOpenReturnsModal={handleOpenReturnsModal}
				/>
			)}
			<Modal
				name='myPurchases.online.details.returns.modal'
				type={MODAL_TYPE.SHEET}
				ref={returnMethodsModalRef}
				title={t('myPurchases.online.details.returns.title')}
				subtitle={
					<ReturnDaysText
						maximumReturnDays={maximumReturnDays}
						orderType={type}
						wrapperComponent='span'
						isModal
					/>
				}
				showTitle
			>
				<ReturnMethodsInfoContainer
					orderId={orderId}
					orderType={type}
					purchaseId={purchaseId}
					unavailableReturnMethods={unavailableReturnMethods}
					barcode={barcode}
					onLocateStoreClick={handleOpenStoreLocatorModal}
				/>
				{order && <ReturnsDownloadForm order={order} />}
			</Modal>

			<StoreLocatorModal storeLocatorModalRef={storeLocatorModalRef} />
		</div>
	)
}
